/**
 * Blacklist matching, mirroring the backend rules (BlacklistItem#matches).
 *
 * Entries are stored verbatim as the operator typed them ("+31 6 57177135" from
 * the settings form, "+31657177135" when added from a conversation), so a raw
 * string comparison would show the contact as not blacklisted even though the
 * backend is dropping their messages.
 */

const PHONE_SHAPED = /^[+()\s.\-0-9]+$/;
const MIN_PHONE_DIGITS = 7;
const MIN_SIGNIFICANT_DIGITS = 9;

const BRAZIL_9TH_DIGIT_DDDS = [
    '11', '12', '13', '14', '15', '16', '17', '18', '19',
    '21', '22', '24', '27', '28',
];

/** Digits only, with the WhatsApp-specific AR '9', MX '1' and BR 9th digit removed. */
function normalizePhoneDigits(value) {
    let digits = String(value).replace(/\D/g, '');

    if (digits.startsWith('549')) digits = `54${digits.slice(3)}`;
    if (digits.startsWith('521')) digits = `52${digits.slice(3)}`;
    if (digits.startsWith('55') && digits.length === 13) {
        const ddd = digits.slice(2, 4);
        if (BRAZIL_9TH_DIGIT_DDDS.includes(ddd) && digits[4] === '9') {
            digits = `55${ddd}${digits.slice(5)}`;
        }
    }

    return digits;
}

function isPhoneShaped(value) {
    return PHONE_SHAPED.test(value) && value.replace(/\D/g, '').length >= MIN_PHONE_DIGITS;
}

function stripLeadingAt(value) {
    return value.startsWith('@') ? value.slice(1) : value;
}

/** True when `itemValue` (a blacklist entry) refers to the same contact as `candidate`. */
export function blacklistValueMatches(itemValue, candidate) {
    if (!itemValue || !candidate) return false;

    const item = String(itemValue).trim();
    const other = String(candidate).trim();
    if (!item || !other) return false;

    if (isPhoneShaped(item) && isPhoneShaped(other)) {
        const left = normalizePhoneDigits(item);
        const right = normalizePhoneDigits(other);
        if (!left || !right) return false;
        if (left === right) return true;

        const [shorter, longer] = left.length <= right.length ? [left, right] : [right, left];
        return shorter.length >= MIN_SIGNIFICANT_DIGITS && longer.endsWith(shorter);
    }

    return stripLeadingAt(item).toLowerCase() === stripLeadingAt(other).toLowerCase();
}

/** Finds the blacklist entry blocking `candidate`, if any. */
export function findBlacklistMatch(items, candidate) {
    if (!Array.isArray(items)) return undefined;
    return items.find((item) => blacklistValueMatches(item?.value, candidate));
}
